import { capitalize } from "~/assets/js/stringUtils";
import NavHeaderTransformer from "~/transformers/NavHeaderTransformer";

export type NavTab = {
  id: string;
  label: string;
  icon: string;
  closable: boolean;
  active: boolean;
  iconOnly?: boolean;
};

type Sublink = {
  icon?: string;
  textLink?: string;
  link?: string;
  closable?: boolean;
};

/**
 * Returns the tab id that best matches the current path (longest prefix wins).
 * "/" only matches when path is exactly "/".
 */
export const resolveActiveTabId = (tabIds: string[], currentPath: string): string | null => {
  return (
    tabIds
      .filter((id) => id === currentPath || (id !== "/" && currentPath.startsWith(`${id}/`)))
      .sort((a, b) => b.length - a.length)[0] ?? null
  );
};

type HomeTabContext = {
  isFederated?: boolean;
  instanceId?: string;
};

/**
 * Fixed home tab (always shown left of agency tabs). In a local agency it links
 * to the `/agencies` list (building icon); inside a federated instance it links
 * back to the parent instance `/federated/{instanceId}` (network icon).
 *
 * The federated context is derived from `currentPath` first: any route under
 * `/federated/{instanceId}` is federated — including the instance root page
 * itself, where `selectedInstance` is not set — so the home tab still shows the
 * network icon there. Falls back to the explicit `context` otherwise.
 */
export const toHomeTab = (currentPath: string, context: HomeTabContext = {}): NavTab => {
  const routeInstanceId = currentPath.match(/^\/federated\/([^/]+)/)?.[1];
  const instanceId = routeInstanceId ?? context.instanceId;
  const isFederated = Boolean(routeInstanceId) || Boolean(context.isFederated);
  const id = isFederated && instanceId ? `/federated/${instanceId}` : "/agencies";
  return {
    id,
    label: "nav.tabs.homeLabel",
    icon: isFederated ? "s2-icon-network-wired-solid" : "s2-icon-building-solid",
    closable: false,
    active: currentPath === id,
    iconOnly: true,
  };
};

/**
 * Agency/instance sublinks (already filtered by visible) → page tabs.
 * `id` = link path, used for navigation and active-route matching.
 * `label` = capitalized textLink (agency name, not an i18n key); mirrors the
 * old NavLink which rendered `capitalize($t(textLink))`.
 */
export const toAgencyTabs = (sublinks: Sublink[], currentPath: string): NavTab[] => {
  const tabIds = sublinks.map((s) => s.link as string);
  const activeId = resolveActiveTabId(tabIds, currentPath);
  return sublinks.map((sublink) => ({
    id: sublink.link as string,
    label: capitalize(sublink.textLink ?? ""),
    icon: NavHeaderTransformer.toIconClass(sublink.icon),
    closable: sublink.closable ?? true,
    active: sublink.link === activeId,
  }));
};
